import { useMemo } from 'react';
import { Box, IconButton } from '@material-ui/core';
import { useMutation } from '@tanstack/react-query';

import { SText } from '@setvi/shared/components';
import { Product } from '@setvi/shared/interfaces';
import { SlackMessageTypes } from '@setvi/shared/enums';
import { sendMessageToSlackChannel } from '@setvi/shared/services';
import { ThumbsDownIcon, ThumbsUpIcon } from '@setvi/shared/components/sicons';

import { useAppContext } from 'Providers/AppProvider/AppContext';

import { ConversationProps } from '../../hook/use-ask-ai';
import { cleanAndStripHTML } from '../../utils';

interface ChatFeedbackProps {
  id: number;
  product: Product;
  conversation: ConversationProps[];
  setConversation: React.Dispatch<React.SetStateAction<ConversationProps[]>>;
}

const ChatFeedback = ({
  id,
  product,
  conversation,
  setConversation
}: ChatFeedbackProps) => {
  const { user } = useAppContext();
  const clientRating = useMemo(
    () => conversation?.find(c => c?.id === id)?.feedback,
    [conversation, id]
  );

  const { mutateAsync: sendFeedback } = useMutation(
    sendMessageToSlackChannel()
  );

  const handleFeedbackClick = async ({
    feedback,
    rating
  }: {
    feedback: string;
    rating?: 'good' | 'bad';
  }) => {
    setConversation(prev =>
      prev?.map(p => ({
        ...p,
        feedback: p?.id === id ? rating : p?.feedback
      }))
    );

    const body = {
      _id: process.env.SLACK_CHANNEL_ID,
      type: SlackMessageTypes.SalesEnablement,
      ...(rating ? { rating } : {}),
      feedback,
      user: {
        email: user?.Email,
        userId: user?.ID,
        company: user?.CompanyName,
        fullName: user?.FullName
      },
      results: {
        type: 'AI Generated',
        data: [product],
        value: JSON.stringify({
          question: conversation?.[id - 1]?.content || '',
          answer: conversation?.[id]?.content
            ? cleanAndStripHTML(conversation?.[id]?.content)
            : ''
        })
      }
    };

    await sendFeedback(body);
  };

  const icontStyle = {
    padding: 8
  };

  return (
    <Box>
      <SText title="Was it helpful?" fontColor="#5D7083" noWrap size="sm" />

      <Box>
        <IconButton
          style={{
            ...icontStyle,
            background: clientRating === 'good' ? '#EEF0F2' : 'transparent'
          }}
          onClick={() =>
            handleFeedbackClick({ feedback: 'good', rating: 'good' })
          }>
          <ThumbsUpIcon
            fill={clientRating === 'good' ? '#333D47' : '#inherit'}
            style={{ width: 18 }}
          />
        </IconButton>
        <IconButton
          style={{
            ...icontStyle,
            background: clientRating === 'bad' ? '#EEF0F2' : 'transparent'
          }}
          onClick={() =>
            handleFeedbackClick({ feedback: 'bad', rating: 'bad' })
          }>
          <ThumbsDownIcon
            fill={clientRating === 'bad' ? '#333D47' : '#inherit'}
            style={{ width: 18 }}
          />
        </IconButton>
      </Box>

      {!!clientRating && (
        <SText
          title="Thank you for your feedback"
          fontColor="#333D47"
          weight="semibold"
          size="sm"
        />
      )}
    </Box>
  );
};

export default ChatFeedback;
